import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios, { AxiosResponse, Cancel } from 'axios';
import { HTTP_STATUSES } from 'libs/constants';
import { getIn } from 'formik';
import { capitalize } from 'libs/utils';

const getMessage = (status: number, data: string[], field?: string): string => {
  const key = field ? ['errors', field] : ['errors'];
  if (Array.isArray(getIn(data, key))) {
    key.push('0');
  }

  const fallbackMessages: Record<number, string> = {
    [HTTP_STATUSES.badRequest]: 'Invalid request. Please check your input and try again',
    [HTTP_STATUSES.unauthorized]: "You're not authorized to perform this action",
    [HTTP_STATUSES.forbidden]: "You don't have permission to perform this action",
    [HTTP_STATUSES.notFound]: 'The requested information could not be found',
    [HTTP_STATUSES.unprocessableEntity]: 'Invalid data. Please check the form and try again',
    [HTTP_STATUSES.gone]: 'Information no longer available',
  };

  return (
    capitalize(getIn(data, key))?.trim() ||
    fallbackMessages[status] ||
    'Network error, please try again.'
  );
};

export default function useHandleHttpError() {
  const navigate = useNavigate();

  const unavailablePageErrors = [
    HTTP_STATUSES.notFound,
    HTTP_STATUSES.unauthorized,
    HTTP_STATUSES.forbidden,
    HTTP_STATUSES.gone,
  ];

  const stayOnPageErrors = [HTTP_STATUSES.badRequest, HTTP_STATUSES.unprocessableEntity];

  return function (
    error: { response: AxiosResponse } | Cancel | unknown,
    field?: string,
    useNotFound?: boolean
  ): void {
    // Can't import Cancel as
    // import { Cancel } from 'axios';
    // Because in this case Cancel is interface but need class.
    if (!(error instanceof axios.Cancel)) {
      const status = getIn(error, 'response.status');
      const data = getIn(error, 'response.data');

      if (useNotFound && status === HTTP_STATUSES.notFound) {
        navigate('/404', { replace: true });
        return;
      }

      if (status === HTTP_STATUSES.gone && data?.redirect_to) {
        const redirect = data.redirect_to;
        if (typeof redirect === 'string' && redirect.startsWith('/')) {
          navigate(redirect, { replace: true });
          return;
        }
      }

      toast.error(getMessage(status, data, field), {
        toastId: status || 500,
      });

      if (unavailablePageErrors.includes(status)) {
        navigate('/unavailable', { replace: true });
        return;
      }

      if (!stayOnPageErrors.includes(status)) {
        navigate('/error', { replace: true });
      }
    }
  };
}
